import Grid from '@mui/material/Grid2'
import {
	Box,
	Typography,
	Card,
	CardContent,
	CardActions,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Button,
	CircularProgress,
	Chip,
	Container,
	Paper,
	Divider,
	TextField,
	Switch,
	FormControlLabel
} from '@mui/material'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useBatch } from '../hooks/useBatch'
import { useCrop } from '../hooks/useCrop'
import { useCategory } from '../hooks/useCategory'
import { calculateLifeStatus } from '../utils/calculateLifeStatus'
import InventoryIcon from '@mui/icons-material/Inventory'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import WarningIcon from '@mui/icons-material/Warning'

export const Inventory = () => {
	const { generateStockReport, loadingReport, stockReport } = useBatch()
	const { crops, loadingList: loadingCrops } = useCrop()
	const { categories, loadingList: loadingCategories } = useCategory()
	const navigate = useNavigate()

	const [filters, setFilters] = useState({
		crop: '',
		germplasm: '',
		category: '',
		batchId: ''
	})

	const [selectedCrop, setSelectedCrop] = useState('')
	const [availableGermplasms, setAvailableGermplasms] = useState<any[]>([])
	const [includeInactive, setIncludeInactive] = useState(false)

	// Actualizar germoplasmas disponibles cuando cambia el cultivo
	useEffect(() => {
		if (selectedCrop) {
			const crop = crops.find(c => c.name === selectedCrop)
			setAvailableGermplasms(crop?.germplasm || [])
			setFilters(prev => ({ ...prev, germplasm: '' })) // Reset germplasm
		} else {
			setAvailableGermplasms([])
		}
	}, [selectedCrop, crops])

	const handleFilterChange = (field: string, value: string) => {
		if (field === 'crop') {
			setSelectedCrop(value)
		}
		setFilters(prev => ({ ...prev, [field]: value }))
	}

	const handleGenerateReport = () => {
		const reportFilters = Object.fromEntries(
			Object.entries(filters).filter(([_, value]) => value !== '')
		)
		if (includeInactive) {
			reportFilters.includeInactive = 'true'
		}
		generateStockReport(reportFilters)
	}

	const handleClearFilters = () => {
		setFilters({ crop: '', germplasm: '', category: '', batchId: '' })
		setSelectedCrop('')
		setIncludeInactive(false)
	}

	const formatNumber = (num: number) => {
		return new Intl.NumberFormat('es-GT', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2
		}).format(num)
	}

	const getStockStatus = (available: number, rejection: number) => {
		const total = available + rejection
		if (total === 0) return { status: 'empty', color: '#9e9e9e', label: 'Sin datos' }
		
		const availablePercentage = (available / total) * 100
		
		if (availablePercentage >= 70) return { status: 'good', color: '#4caf50', label: 'Buen stock' }
		if (availablePercentage >= 40) return { status: 'warning', color: '#ff9800', label: 'Stock medio' }
		return { status: 'low', color: '#f44336', label: 'Stock bajo' }
	}

	const activeFiltersCount = Object.values(filters).filter(value => value !== '').length

	const handleManageBatch = (batchId: string) => {
		navigate('/home/administrar-lote', { state: { batchId } })
	}

	return (
		<Container maxWidth="lg">
			<Box p={3}>
				<Box display="flex" alignItems="center" mb={3}>
					<InventoryIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
					<Typography variant="h4" component="h1">
						Inventario de Semillas
					</Typography>
				</Box>

				{/* Filtros */}
				<Paper elevation={2} sx={{ p: 3, mb: 3 }}>
					<Typography variant="h6" mb={2}>
						Filtros de Búsqueda
					</Typography>
					
					<Grid container spacing={2} mb={2}>
						<Grid size={{ xs: 12, sm: 6, md: 3 }}>
							<FormControl fullWidth>
								<InputLabel>Cultivo</InputLabel>
								<Select
									value={filters.crop}
									onChange={(e) => handleFilterChange('crop', e.target.value)}
									label="Cultivo"
									disabled={loadingCrops}
								>
									<MenuItem value="">Todos</MenuItem>
									{crops.map((crop) => (
										<MenuItem key={crop.uid} value={crop.name}>
											{crop.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>

						<Grid size={{ xs: 12, sm: 6, md: 3 }}>
							<FormControl fullWidth>
								<InputLabel>Germoplasma</InputLabel>
								<Select
									value={filters.germplasm}
									onChange={(e) => handleFilterChange('germplasm', e.target.value)}
									label="Germoplasma"
									disabled={!selectedCrop || availableGermplasms.length === 0}
								>
									<MenuItem value="">Todos</MenuItem>
									{availableGermplasms.map((germ) => (
										<MenuItem key={germ.name} value={germ.name}>
											{germ.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>

						<Grid size={{ xs: 12, sm: 6, md: 3 }}>
							<FormControl fullWidth>
								<InputLabel>Categoría</InputLabel>
								<Select
									value={filters.category}
									onChange={(e) => handleFilterChange('category', e.target.value)}
									label="Categoría"
									disabled={loadingCategories}
								>
									<MenuItem value="">Todas</MenuItem>
									{categories.map((category) => (
										<MenuItem key={category.uid} value={category.name}>
											{category.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>

						<Grid size={{ xs: 12, sm: 6, md: 3 }}>
							<TextField
								fullWidth
								label="No. de Lote"
								placeholder="Buscar por ID de lote..."
								value={filters.batchId}
								onChange={(e) => handleFilterChange('batchId', e.target.value)}
								variant="outlined"
							/>
						</Grid>
					</Grid>

					{/* Botones de acción */}
					<Box display="flex" gap={2} justifyContent="center" mb={2}>
						<Button
							variant="contained"
							onClick={handleGenerateReport}
							disabled={loadingReport}
							sx={{ minWidth: '150px' }}
						>
							{loadingReport ? <CircularProgress size={24} /> : 'Generar Reporte'}
						</Button>
						{activeFiltersCount > 0 && (
							<Button
								variant="outlined"
								onClick={handleClearFilters}
							>
								Limpiar Filtros
							</Button>
						)}
					</Box>

					<Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
						<FormControlLabel
							control={
								<Switch
									checked={includeInactive}
									onChange={(e) => setIncludeInactive(e.target.checked)}
									color="primary"
								/>
							}
							label="Incluir lotes desactivados"
						/>
					</Box>

					{activeFiltersCount > 0 && (
						<Box>
							<Typography variant="body2" color="textSecondary" mb={1}>
								Filtros activos:
							</Typography>
							<Box display="flex" gap={1} flexWrap="wrap">
								{filters.crop && <Chip label={`Cultivo: ${filters.crop}`} size="small" />}
								{filters.germplasm && <Chip label={`Germoplasma: ${filters.germplasm}`} size="small" />}
								{filters.category && <Chip label={`Categoría: ${filters.category}`} size="small" />}
								{filters.batchId && <Chip label={`Lote: ${filters.batchId}`} size="small" />}
							</Box>
						</Box>
					)}
				</Paper>

				{/* Resultados del Reporte */}
				{stockReport && (
					<Paper elevation={2} sx={{ p: 3 }}>
						<Typography variant="h6" mb={3}>
							Resumen de Inventario
						</Typography>

						<Grid container spacing={3}>
							{/* Tarjeta de Semillas Disponibles */}
							<Grid size={{ xs: 12, md: 4 }}>
								<Card sx={{ 
									bgcolor: '#e8f5e8', 
									border: '2px solid #4caf50',
									height: '100%'
								}}>
									<CardContent>
										<Box display="flex" alignItems="center" mb={2}>
											<TrendingUpIcon sx={{ color: '#4caf50', fontSize: 30, mr: 1 }} />
											<Typography variant="h6" color="#2e7d32">
												Disponibles
											</Typography>
										</Box>
										<Typography variant="h4" color="#2e7d32" fontWeight="bold">
											{formatNumber(stockReport.totalAvailable)} qq
										</Typography>
									</CardContent>
								</Card>
							</Grid>

							{/* Tarjeta de Rechazos */}
							<Grid size={{ xs: 12, md: 4 }}>
								<Card sx={{ 
									bgcolor: '#fff3e0', 
									border: '2px solid #ff9800',
									height: '100%'
								}}>
									<CardContent>
										<Box display="flex" alignItems="center" mb={2}>
											<WarningIcon sx={{ color: '#ff9800', fontSize: 30, mr: 1 }} />
											<Typography variant="h6" color="#f57c00">
												Rechazos
											</Typography>
										</Box>
										<Typography variant="h4" color="#f57c00" fontWeight="bold">
											{formatNumber(stockReport.totalRejection)} qq
										</Typography>
									</CardContent>
								</Card>
							</Grid>

							{/* Tarjeta de Pérdidas */}
							<Grid size={{ xs: 12, md: 4 }}>
								<Card sx={{ 
									bgcolor: '#ffebee', 
									border: '2px solid #f44336',
									height: '100%'
								}}>
									<CardContent>
										<Box display="flex" alignItems="center" mb={2}>
											<TrendingDownIcon sx={{ color: '#f44336', fontSize: 30, mr: 1 }} />
											<Typography variant="h6" color="#d32f2f">
												Pérdidas
											</Typography>
										</Box>
										<Typography variant="h4" color="#d32f2f" fontWeight="bold">
											{formatNumber(stockReport.totalLosses)} qq
										</Typography>
									</CardContent>
								</Card>
							</Grid>
						</Grid>

						<Divider sx={{ my: 3 }} />

						{/* Resumen Total */}
						<Box textAlign="center">
							<Typography variant="h6" mb={2}>
								Total de Inventario
							</Typography>
							<Typography variant="h3" fontWeight="bold" color="primary.main">
								{formatNumber(stockReport.totalAvailable + stockReport.totalRejection)} qq
							</Typography>
							
							{(() => {
								const status = getStockStatus(
									stockReport.totalAvailable, 
									stockReport.totalRejection, 
								)
								return (
									<Chip 
										label={status.label}
										sx={{ 
											bgcolor: status.color, 
											color: 'white',
											fontWeight: 'bold',
											mt: 2
										}} 
									/>
								)
							})()}
						</Box>
					</Paper>
				)}

				{/* Lista de Lotes */}
				{stockReport && stockReport.batches && stockReport.batches.length > 0 && (
					<Paper elevation={2} sx={{ p: 3, mt: 3 }}>
						<Typography variant="h6" mb={3}>
							Lotes Correspondientes ({stockReport.batches.length})
						</Typography>

						<Grid container spacing={2}>
							{stockReport.batches.map((lote) => {
								const title = `No. de Lote: ${lote.batchId}`
								const status = calculateLifeStatus(lote.date, lote.expiration)
								const statusColor = {
									success: '#4caf50',
									warning: '#ff9800',
									error: '#f44336'
								}[status]

								return (
									<Grid key={lote.uid} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
										<Card
											variant="outlined"
											sx={{
												height: '100%',
												bgcolor: 'background.paper',
												boxShadow: 2,
												borderRadius: 2,
												position: 'relative',
												'&:hover': {
													boxShadow: 4,
													transform: 'translateY(-2px)',
													transition: 'all 0.3s ease-in-out'
												}
											}}
										>
											<Box
												sx={{
													width: 20,
													height: 20,
													borderRadius: '50%',
													backgroundColor: statusColor,
													position: 'absolute',
													top: 16,
													right: 16,
													zIndex: 1
												}}
											/>
											<CardContent sx={{ pb: 1 }}>
												<Typography variant="h6" component="h3" gutterBottom>
													{title}
												</Typography>
												<Typography variant="body2" color="textSecondary" mb={2}>
													Fecha de Ingreso: {new Date(lote.date).toLocaleDateString('es-GT')}
												</Typography>
												
												<Box sx={{ mb: 2 }}>
													<Typography variant="body2" mb={1}>
														<strong>Cultivo:</strong> {lote.crop}
													</Typography>
													<Typography variant="body2" mb={1}>
														<strong>Categoría:</strong> {lote.category}
													</Typography>
													<Typography variant="body2" mb={1}>
														<strong>Germoplasma:</strong> {lote.germplasm}
													</Typography>
													<Typography variant="body2" mb={1}>
														<strong>Propietario:</strong> {lote.owner}
													</Typography>
													<Typography variant="body2" mb={1}>
														<strong>Procedencia:</strong> {lote.origin}
													</Typography>
												</Box>

												<Divider sx={{ my: 2 }} />
												
												<Box>
													<Typography variant="body2" color="primary" mb={1}>
														<strong>Stock Disponible:</strong> {formatNumber(lote.available)} qq
													</Typography>
													<Typography variant="body2" color="warning.main" mb={1}>
														<strong>Rechazos:</strong> {formatNumber(lote.rejection)} qq
													</Typography>
													<Typography variant="body2" color="error.main">
														<strong>Pérdidas:</strong> {formatNumber(lote.losses)} qq
													</Typography>
												</Box>
											</CardContent>
											<CardActions sx={{ justifyContent: 'center', pb: 2 }}>
												<Button
													variant="contained"
													size="small"
													onClick={() => handleManageBatch(lote.uid)}
													sx={{ minWidth: '140px' }}
												>
													Administrar Lote
												</Button>
											</CardActions>
										</Card>
									</Grid>
								)
							})}
						</Grid>
					</Paper>
				)}

				{/* Estado inicial */}
				{!stockReport && !loadingReport && (
					<Paper elevation={1} sx={{ p: 6, textAlign: 'center', bgcolor: '#f5f5f5' }}>
						<InventoryIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
						<Typography variant="h6" color="textSecondary" mb={1}>
							Generar Reporte de Inventario
						</Typography>
						<Typography variant="body2" color="textSecondary">
							Selecciona los filtros deseados y haz clic en "Generar Reporte" para ver el estado actual del inventario.
						</Typography>
					</Paper>
				)}
			</Box>
		</Container>
	)
}
